import { useEffect, useState } from "react";
import { CloudCheck, Download, FolderClock, RefreshCw, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  Badge,
  Button,
  EmptyState,
  PageSection,
  Skeleton,
  StatusMessage,
  SteadyImage,
  cn,
} from "@ceedero/ui";
import { useTranslation } from "@/localization/hooks/use-translation";
import {
  useProjectVersions,
  useRemoveProjectVersions,
} from "@/project-history/hooks/use-project-versions";
import { daysUntilExpiry } from "@/project-history/utils/version-expiry";
import { projectVersionResourceUrl } from "@/project-history/utils/version-links";
import { versionDescription } from "@/project-history/utils/version-description";

// Which removal the open confirmation is asking about: one version, or all of them.
type PendingRemoval = { id: string } | "all";

export const ProjectVersionHistory = ({
  projectId,
  title,
}: {
  projectId: string;
  title: string;
}) => {
  const { t, language } = useTranslation();
  const history = useProjectVersions(projectId);
  const removal = useRemoveProjectVersions(projectId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval>();
  const [currentTime, setCurrentTime] = useState(() => new Date().toISOString());
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date().toISOString()), 1000);
    return () => clearInterval(interval);
  }, []);
  const versions =
    history.data?.pages
      .flatMap((page) => page.versions)
      .filter((version) => version.expiresAt > currentTime) ?? [];
  const selected = versions.find((version) => version.id === selectedId) ?? versions[0];
  const date = (value: string) =>
    new Intl.DateTimeFormat(language, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(value),
    );
  const confirmRemoval = () => {
    if (pendingRemoval) {
      removal.mutate(pendingRemoval === "all" ? undefined : pendingRemoval.id);
    }
    setPendingRemoval(undefined);
  };
  return (
    <PageSection
      title={t("history.timeline")}
      description={t("history.retention")}
      actions={
        <div className="flex flex-wrap gap-2">
          {versions.length > 0 && (
            <Button
              variant="destructiveGhost"
              onClick={() => setPendingRemoval("all")}
              isLoading={removal.isPending && removal.variables === undefined}
            >
              <Trash2 aria-hidden />
              {t("history.removeAll")}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => history.refetch()}
            isLoading={history.isFetching}
          >
            <RefreshCw aria-hidden />
            {t("history.refresh")}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4">
        <p className="text-sm text-muted-foreground">{t("history.webDescription")}</p>
        {history.isPending && <Skeleton className="h-40 w-full" />}
        {history.isError && <StatusMessage tone="error">{t("history.failed")}</StatusMessage>}
        {removal.isError && <StatusMessage tone="error">{t("history.removeFailed")}</StatusMessage>}
        {history.isSuccess && !versions.length && (
          <EmptyState
            icon={<FolderClock />}
            title={t("history.emptyTitle")}
            description={t("history.webEmptyDescription")}
          />
        )}
        {selected && (
          <div className="grid items-start gap-6 lg:grid-cols-[minmax(14rem,20rem)_minmax(0,1fr)]">
            <div className="grid gap-2">
              {versions.map((version) => (
                <Button
                  key={version.id}
                  variant="outline"
                  className={cn(
                    "h-auto justify-start whitespace-normal p-3 text-left",
                    version.id === selected.id && "ring-2 ring-primary-edge",
                  )}
                  onClick={() => setSelectedId(version.id)}
                >
                  <span className="grid gap-1">
                    <span>
                      {versionDescription({ changes: version.changes ?? [], t, language })}
                    </span>
                    <span className="text-xs text-muted-foreground">{date(version.createdAt)}</span>
                    <Badge variant="inline">
                      <CloudCheck aria-hidden />
                      {t("history.inCloud")}
                    </Badge>
                  </span>
                </Button>
              ))}
              {history.hasNextPage && (
                <Button
                  variant="outline"
                  onClick={() => history.fetchNextPage()}
                  isLoading={history.isFetchingNextPage}
                >
                  {t("history.loadMore")}
                </Button>
              )}
            </div>
            {/* Pinned while the list beside it scrolls, so a version is compared without scrolling back up. */}
            <div className="grid gap-4 lg:sticky lg:top-6">
              {selected.previewSha256 ? (
                <SteadyImage
                  src={projectVersionResourceUrl({
                    projectId,
                    id: selected.id,
                    resource: "preview",
                  })}
                  alt={t("history.previewAlt")}
                  className="min-h-56 rounded-md border border-border bg-card p-4"
                  imageClassName="max-h-96 w-full object-contain"
                />
              ) : (
                <div className="grid min-h-56 place-items-center rounded-md border border-border bg-card p-4">
                  <FolderClock aria-hidden className="size-12 text-muted-foreground" />
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                {t("history.expiresIn", {
                  date: date(selected.expiresAt),
                  count: daysUntilExpiry({ expiresAt: selected.expiresAt, now: currentTime }),
                })}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button asChild className="w-fit">
                  <a
                    href={projectVersionResourceUrl({
                      projectId,
                      id: selected.id,
                      resource: "contents",
                    })}
                  >
                    <Download aria-hidden />
                    {t("history.download")}
                  </a>
                </Button>
                <Button
                  variant="destructiveGhost"
                  onClick={() => setPendingRemoval({ id: selected.id })}
                  isLoading={removal.isPending && removal.variables === selected.id}
                >
                  <Trash2 aria-hidden />
                  {t("history.remove")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <AlertDialog
        open={pendingRemoval !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setPendingRemoval(undefined);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogTitle>
            {pendingRemoval === "all"
              ? t("history.removeAllTitle", { title })
              : t("history.removeTitle")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {pendingRemoval === "all"
              ? t("history.removeAllDescription")
              : t("history.removeDescription")}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="ghost">
                {t("account.cancel")}
              </Button>
            </AlertDialogCancel>
            <Button type="button" variant="destructive" onClick={confirmRemoval}>
              {pendingRemoval === "all" ? t("history.removeAll") : t("history.remove")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageSection>
  );
};
